import type { UseFormReturn } from 'react-hook-form';
import type { IFieldElem } from '@models/IFormData';
import { FormulaManager } from '@utils/managers/FormulaManager';
import type { IFormulaRequestCache } from '@utils/managers/IFormulaRequestCache';
import type { TableFormulaEvaluator } from './tableValidation';

/** FormulaManager-адаптер для чистого table validator поверх resolver values. */
/** Разрешение аргументов правила: `|Document` и `#links` — из значений документа,
 *  `|Parent` — из родительской строки, остальное — из строки. Единая семантика для
 *  вычисления и для ключа кэша результатов в сводке заполнения (createCompletionEvaluator). */
export const resolveTableRuleFieldValues = (
    fieldNames: readonly string[],
    fields: Record<string, IFieldElem>,
    getValues: (name: string) => unknown,
    row?: unknown,
    parentRow?: unknown,
): unknown[] => {
    const rowValues = row as Record<string, unknown> | undefined;
    const parentValues = parentRow as Record<string, unknown> | undefined;
    return fieldNames.map((name) => {
        if (name.includes('|Document') || name.startsWith('#links')) {
            const field = fields[name];
            return field ? getValues(field.name) : undefined;
        }
        if (name.includes('|Parent') && parentValues) return parentValues[name.replace('|Parent', '')];
        return rowValues?.[name];
    });
};

export const createDocumentTableFormulaEvaluator = (
    fields: Record<string, IFieldElem>,
    getValues: (name?: string) => unknown,
    isEdit: boolean,
    isNew: boolean,
    requestCache?: IFormulaRequestCache,
    onEvaluation?: () => void,
    onError?: (error: unknown) => void,
): TableFormulaEvaluator => {
    const formMethods = { getValues } as unknown as UseFormReturn<any>;

    return async (rule, rowValue, parentValue) => {
        onEvaluation?.();
        const row = rowValue as Record<string, unknown> | undefined;
        const parentRow = parentValue as Record<string, unknown> | undefined;
        const manager = new FormulaManager(rule, undefined, requestCache);
        manager.Init(fields, formMethods, parentRow);
        const values = resolveTableRuleFieldValues(manager.GetFields(), fields, getValues, row, parentRow);

        try {
            return await manager.EvalFormulaValues(
                isEdit,
                isNew,
                values.every((value) => value === undefined) ? undefined : values,
            );
        } catch (error) {
            onError?.(error);
            console.error(error);
            return false;
        }
    };
};
